import { useMemo } from "react"

type Serializer = (input: unknown, seen: WeakSet<object>) => string

const serializeObject = (
  obj: Record<string, unknown>,
  seen: WeakSet<object>,
  serialize: Serializer
): string => {
  if (seen.has(obj)) return '"__circular__"'
  seen.add(obj)

  const keys = Object.keys(obj).sort()
  const entries = keys.map(
    (key) => `${JSON.stringify(key)}:${serialize(obj[key], seen)}`
  )
  return `{${entries.join(",")}}`
}

const serializeArray = (
  arr: unknown[],
  seen: WeakSet<object>,
  serialize: Serializer
): string => `[${arr.map((value) => serialize(value, seen)).join(",")}]`

const serializePrimitiveByType: Record<string, (value: unknown) => string> = {
  bigint: (value) => `"${String(value)}n"`,
  boolean: (value) => (value ? "true" : "false"),
  function: () => '"__function__"',
  number: (value) => (Object.is(value, -0) ? '"-0"' : String(value)),
  string: (value) => JSON.stringify(value),
  symbol: () => '"__symbol__"',
  undefined: () => '"__undefined__"'
}

const stableStringify = (value: unknown): string => {
  const seen = new WeakSet<object>()

  const serialize: Serializer = (input, seenSet) => {
    if (input === null) return "null"

    const byType = serializePrimitiveByType[typeof input]
    if (byType) return byType(input)

    if (input instanceof Date) return `{"__date__":${input.toISOString()}}`
    if (input instanceof RegExp)
      return `{"__regexp__":${JSON.stringify(String(input))}}`

    if (Array.isArray(input)) return serializeArray(input, seenSet, serialize)

    if (typeof input === "object") {
      return serializeObject(
        input as Record<string, unknown>,
        seenSet,
        serialize
      )
    }

    return '"__unknown__"'
  }

  return serialize(value, seen)
}

/**
 * useDeepCompareMemoize
 *
 * @description
 * Keep a stable reference to `value` across renders when it's deeply equal to the previous value.
 * Uses Ramda `equals` for deep comparison.
 * @template T
 * @param {T} value - The value to memoize by deep equality.
 * @returns {T} The previous reference when deep-equal, otherwise the new value.
 *
 */
const useDeepCompareMemoize = <T>(value: T): T => {
  const stableKey = useMemo(() => stableStringify(value), [value])

  // eslint-disable-next-line react-hooks/exhaustive-deps -- stableKey captures deep equality; omitting `value` keeps the reference stable.
  return useMemo(() => value, [stableKey])
}

export default useDeepCompareMemoize
